import { ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatAutocompleteTrigger} from '@angular/material/autocomplete';
import { FormsBuilderTutorialComponent } from '../tutorial/forms-builder-tutorial.component';
import {MatSelectChange} from '@angular/material/select';

import {FormsGlobals} from '../../../../models/forms/forms.enum';
import {FormsBuilderPolishedService} from '../services/forms-builder-polished.service';
import {EnumValidValueItemModel} from '../../../../models/enum/enum-valid-value-item.model';
import {EnumValidValueModel} from '../../../../models/enum/enum-valid-value.model';
import {FormItemConfigurationModel} from '../../../../models/forms/form-item-configuration.model';
import {FormConfigurationModel} from '../../../../models/forms/form-configuration.model';
import {FormItemActionConfigurationModel} from '../../../../models/forms/form-item-action-configuration.model';
import {FormsConstants} from '../../models/forms-constants';
import { StaticDataService } from 'src/app/services/static-data.service';

@Component({
  standalone: false,
  selector: 'app-forms-builder-polished-item',
  templateUrl: './forms-builder-polished-item.component.html',
  styleUrls: ['./forms-builder-polished-item.component.css']
})
export class FormsBuilderPolishedItemComponent implements OnInit, OnDestroy {
  private static readonly SEARCH_FILTER_DELAY_MS = 250;

  item: FormItemConfigurationModel;
  originalItem: FormConfigurationModel;

  isLoading: boolean = false;
  canUseValidValues: boolean = true;
  canUseDefaultValue: boolean = true;
  canUseAfterUpdateAction: boolean = true;
  canSelectDefaultFromValidValue: boolean = true;

  cfgItemAlignmentTypeList: any[] = [];
  cfgFormItemTypeList: any[] = [];
  otherItemsInForm: FormItemConfigurationModel[] = [];
  validValues: EnumValidValueModel[] = [];
  filteredValidValues: EnumValidValueModel[] = [];
  actionList: FormItemActionConfigurationModel[] = [];

  selectedBeforeActionId: string;
  selectedAfterActionId: string;
  selectedParentItemId: string;
  selectedAlignmentType: string;
  selectedValidValue: EnumValidValueModel;
  selectedControlTypeId: string;
  selectedValidValueId: string;
  valueTriggerList: EnumValidValueItemModel[] = [];
  filteredSelectedValidValueItems: EnumValidValueItemModel[] = [];
  selectedValueTriggerId: string;
  canValidValueControlTypes: string[] = [];
  canUseDefaultValueTypes: string[] = [];
  validValueSearchQuery: string = '';
  defaultValueSearchQuery: string = '';
  // In-panel search state for the mat-select dropdowns: one query plus one
  // filtered list per dropdown, kept in sync by the onXSearchInput handlers.
  controlTypeSearchQuery: string = '';
  parentItemSearchQuery: string = '';
  beforeActionSearchQuery: string = '';
  afterActionSearchQuery: string = '';
  valueTriggerSearchQuery: string = '';
  filteredControlTypes: any[] = [];
  filteredOtherItemsInForm: FormItemConfigurationModel[] = [];
  filteredBeforeActions: FormItemActionConfigurationModel[] = [];
  filteredAfterActions: FormItemActionConfigurationModel[] = [];
  filteredValueTriggers: EnumValidValueItemModel[] = [];
  isFilteringValidValues: boolean = false;
  isFilteringDefaultValues: boolean = false;
  @ViewChild('validValueTrigger') validValueTrigger?: MatAutocompleteTrigger;
  @ViewChild('defaultValueTrigger') defaultValueTrigger?: MatAutocompleteTrigger;
  @ViewChild('defaultValueInput') defaultValueInput?: ElementRef<HTMLInputElement>;
  valuesPanelExpanded: boolean = true;
  layoutPanelExpanded: boolean = true;
  hierarchyPanelExpanded: boolean = false;
  advancedPanelExpanded: boolean = false;
  private validValueSearchIndex = new Map<string, string>();
  private validValueFilterTimer: ReturnType<typeof setTimeout> | null = null;
  private defaultValueFilterTimer: ReturnType<typeof setTimeout> | null = null;
  private defaultValueFocusTimer: ReturnType<typeof setTimeout> | null = null;
  
  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly globals: FormsGlobals,
    private readonly formsBuilderService: FormsBuilderPolishedService,
    public readonly dialogRef: MatDialogRef<FormsBuilderPolishedItemComponent>,
    private readonly staticDataService: StaticDataService,
    private readonly dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {

    this.canValidValueControlTypes = [
      this.globals.CONTROL_ACTION_BUTTON,
      this.globals.CONTROL_BLOOD_PRESSURE,
      this.globals.CONTROL_CURRENCY,
      this.globals.CONTROL_DATE,
      this.globals.CONTROL_DATE_TIME,
      this.globals.CONTROL_GROUP_HEADING_WITH_SUMMARY,
      this.globals.CONTROL_HEADING,
      this.globals.CONTROL_LABEL,
      this.globals.CONTROL_LABEL_45,
      this.globals.CONTROL_NUMERIC_DECIMAL,
      this.globals.CONTROL_NUMERIC_DRAG_INPUT_SLIDER,
      this.globals.CONTROL_NUMERIC_INTEGER,
      this.globals.CONTROL_NUMERIC_SLIDER,
      this.globals.CONTROL_ROW_HEADER,
      this.globals.CONTROL_SIGNATURE,
      this.globals.CONTROL_SPACER,
      this.globals.CONTROL_TEXT_AREA,
      this.globals.CONTROL_TEXT_BOX,
      this.globals.CONTROL_TIME,
      this.globals.CONTROL_VITAL_SIGNS,
      this.globals.CONTROL_PHONE,
      this.globals.CONTROL_HIDDENINPUT,
      this.globals.CONTROL_MARKUP,
      this.globals.CONTROL_MACRO_NOTES_DROPDOWN,
      this.globals.CONTROL_IMAGE_LIST
    ];

    this.canUseDefaultValueTypes = [
      this.globals.CONTROL_ACTION_BUTTON,
      this.globals.CONTROL_GROUP_HEADING_WITH_SUMMARY,
      this.globals.CONTROL_HEADING,
      this.globals.CONTROL_LABEL,
      this.globals.CONTROL_LABEL_45,
      this.globals.CONTROL_ROW_HEADER,
      this.globals.CONTROL_SIGNATURE,
      this.globals.CONTROL_SPACER,
      this.globals.CONTROL_PHONE,
      this.globals.CONTROL_HIDDENINPUT,
      // this.globals.CONTROL_MARKUP,
    ];

  }

  
  ngOnInit() {
    this.setupAllFields();
    this.originalItem = this.data.cfgItem;
    this.validValues = [...this.data.validValues].sort((a: EnumValidValueModel, b: EnumValidValueModel) => {
      if (a.ValidValueName.toLowerCase() < b.ValidValueName.toLowerCase()) { return -1; }
      if (a.ValidValueName.toLowerCase() > b.ValidValueName.toLowerCase()) { return 1; }
      return 0;
    });
    this.actionList = this.data.actionList;
    this.otherItemsInForm = this.data.otherItems.sort((a: FormItemConfigurationModel, b: FormItemConfigurationModel) => {
      if (a.DFormItemName.toLowerCase() < b.DFormItemName.toLowerCase()) { return -1; }
      if (a.DFormItemName.toLowerCase() > b.DFormItemName.toLowerCase()) { return 1; }
      return 0;
    });
    console.log('Init this.data.cfgItem', this.data.cfgItem);
    this.item = JSON.parse(JSON.stringify(this.data.cfgItem));
    this.buildValidValueSearchIndex();
    this.filteredValidValues = this.validValues;
    this.selectedControlTypeId = this.item.enumFormControlID;
    const initialValidValueId = this.item.ValidValueId || this.item.ValidValue?.ValidValueId || null;
    this.selectedValidValueId = initialValidValueId;
    this.selectedValidValue = this.getValidValueById(initialValidValueId) || this.item.ValidValue || null;
    this.item.ValidValueId = this.selectedValidValue?.ValidValueId || this.selectedValidValueId;
    this.validValueSearchQuery = this.selectedValidValue?.ValidValueName || '';
    this.selectedParentItemId = this.item.ParentDFormItemId;
    this.selectedAlignmentType = this.item.enumLabelAligmentID;
    this.selectedAfterActionId = this.item.AfterUpdate_ItemActionId;
    this.selectedBeforeActionId = this.item.BeforeDisplay_ItemActionId;
    this.selectedValueTriggerId = this.item.NoteTriggerValidValueItemId; 
    
    // Populate valueTriggerList if valid value is already selected
    if (this.selectedValidValueId) {
      this.valueTriggerList = this.staticDataService.getValidValueItemsByValidValueId(this.selectedValidValueId);
      this.filteredSelectedValidValueItems = this.valueTriggerList;
      this.defaultValueSearchQuery = this.getDefaultValueDisplayText(this.item.DefaultValue);
      console.log('valueTriggerList initialized in ngOnInit', this.valueTriggerList);
    }

    this.resetSelectSearchFilters();
    this.checkOptions();
    this.hierarchyPanelExpanded = !!(this.item.ParentDFormItemId || this.item.HasChildren);
    this.advancedPanelExpanded = !!(
      this.item.BeforeDisplay_ItemActionId ||
      this.item.AfterUpdate_ItemActionId ||
      this.item.isNoteEnabled
    );
  }

  private getValidValueById(validValueId: string | null | undefined): EnumValidValueModel | null {
    if (!validValueId) {
      return null;
    }

    return this.validValues.find(
      value => value.ValidValueId?.toLowerCase() === validValueId.toLowerCase()
    ) || null;
  }

  

  selectDefaultValue(validValueItem: EnumValidValueItemModel) {
    this.item.DefaultValue = validValueItem.ValidValueItemId;
    this.defaultValueSearchQuery = validValueItem.ItemDisplayValue;
    this.applyDefaultValueFilter();
  }

  setupAllFields() {
    this.setupItemTypeList();
    this.setupAlignmentList();
  }

  selectControlType(controlType) {
    console.log('selectControlType executed', controlType);
    this.selectedControlTypeId = controlType.id;
    this.item.enumFormControl = null;
    this.item.enumFormControlID = this.selectedControlTypeId;
    this.checkOptions();
  }

  removeDefaultValue() {
    this.item.DefaultValue = null;
  }

  selectParentItem(parentItem: FormItemConfigurationModel) {
    this.selectedParentItemId = parentItem.DFormItemId;
    this.item.ParentDFormItemId = parentItem.DFormItemId;
  }

  selectValidValue(validValue: EnumValidValueModel) {
    console.log('selectValidValue', validValue);
    this.clearValidValueFilterTimer();
    this.isFilteringValidValues = false;
    const previousValidValueId = this.selectedValidValueId;
    this.selectedValidValueId = validValue.ValidValueId;
    this.selectedValidValue = validValue;
    this.validValueSearchQuery = validValue.ValidValueName;
    this.item.ValidValue = null;
    this.item.ValidValueId = this.selectedValidValueId;
    this.valueTriggerList = this.staticDataService.getValidValueItemsByValidValueId(this.selectedValidValueId);
    this.filteredSelectedValidValueItems = this.valueTriggerList;
    // The value trigger options come from the valid value, so its dropdown filter
    // has to be rebuilt against the new list.
    this.onValueTriggerSearchInput('');
    if (previousValidValueId !== this.selectedValidValueId) {
      this.item.DefaultValue = null;
    }
    this.defaultValueSearchQuery = this.getDefaultValueDisplayText(this.item.DefaultValue);
    this.applyDefaultValueFilter();
    // Open the default value options as soon as a list is picked. Focusing the input (rather than
    // calling openPanel) opens them and moves keyboard focus off Valid Values, which the autocomplete
    // re-focuses after a selection. The Default Value field only renders once selectedValidValue is
    // set, so the input exists after the next change detection.
    if (this.defaultValueSearchQuery || this.filteredSelectedValidValueItems.length > 0) {
      this.clearDefaultValueFocusTimer();
      this.defaultValueFocusTimer = setTimeout(() => {
        this.defaultValueFocusTimer = null;
        this.defaultValueInput?.nativeElement.focus();
      });
    }
    console.log('valueTriggerList', this.valueTriggerList);
    this.checkOptions();
  }

  ngOnDestroy(): void {
    this.clearValidValueFilterTimer();
    this.clearDefaultValueFilterTimer();
    this.clearDefaultValueFocusTimer();
  }

  // The dialog body scrolls inside .section-content, which the CDK overlay doesn't track, so the
  // open autocomplete panels are repositioned by hand to stay attached to their inputs.
  repositionValueAutocompletes(): void {
    if (this.validValueTrigger?.panelOpen) {
      this.validValueTrigger.updatePosition();
    }
    if (this.defaultValueTrigger?.panelOpen) {
      this.defaultValueTrigger.updatePosition();
    }
  }

  onInlineValidValueSelected(validValue: EnumValidValueModel): void {
    this.selectValidValue(validValue);
  }

  onValidValueSearchInput(inputValue: string): void {
    this.validValueSearchQuery = inputValue;
    const query = this.validValueSearchQuery.trim();

    this.clearValidValueFilterTimer();

    if (!query) {
      this.selectedValidValueId = null;
      this.selectedValidValue = null;
      this.item.ValidValue = null;
      this.item.ValidValueId = null;
      this.item.DefaultValue = null;
      this.defaultValueSearchQuery = '';
      this.valueTriggerList = [];
      this.filteredSelectedValidValueItems = [];
      this.isFilteringValidValues = false;
      this.applyValidValueFilter();
      return;
    }

    this.isFilteringValidValues = true;
    this.validValueFilterTimer = setTimeout(() => {
      this.applyValidValueFilter();
      this.isFilteringValidValues = false;
      this.validValueFilterTimer = null;
      this.cdr.detectChanges();
    }, FormsBuilderPolishedItemComponent.SEARCH_FILTER_DELAY_MS);
  }

  clearValidValueSearch(): void {
    this.validValueSearchQuery = '';
    this.onValidValueSearchInput('');
  }

  clearDefaultValueSearch(): void {
    this.clearDefaultValueFilterTimer();
    this.isFilteringDefaultValues = false;
    this.defaultValueSearchQuery = '';
    this.item.DefaultValue = null;
    this.onDefaultValueSearchInput('');
  }

  onInlineDefaultValueSelected(validValueItem: EnumValidValueItemModel): void {
    this.selectDefaultValue(validValueItem);
  }

  onDefaultValueSearchInput(inputValue: string): void {
    this.defaultValueSearchQuery = inputValue;
    const query = this.defaultValueSearchQuery.trim();

    this.clearDefaultValueFilterTimer();

    if (!query) {
      this.item.DefaultValue = null;
      this.isFilteringDefaultValues = false;
      this.applyDefaultValueFilter();
      return;
    }

    this.isFilteringDefaultValues = true;
    this.defaultValueFilterTimer = setTimeout(() => {
      this.applyDefaultValueFilter();
      this.isFilteringDefaultValues = false;
      this.defaultValueFilterTimer = null;
      this.cdr.detectChanges();
    }, FormsBuilderPolishedItemComponent.SEARCH_FILTER_DELAY_MS);
  }

  private clearValidValueFilterTimer(): void {
    if (this.validValueFilterTimer) {
      clearTimeout(this.validValueFilterTimer);
      this.validValueFilterTimer = null;
    }
  }

  private clearDefaultValueFilterTimer(): void {
    if (this.defaultValueFilterTimer) {
      clearTimeout(this.defaultValueFilterTimer);
      this.defaultValueFilterTimer = null;
    }
  }

  private clearDefaultValueFocusTimer(): void {
    if (this.defaultValueFocusTimer) {
      clearTimeout(this.defaultValueFocusTimer);
      this.defaultValueFocusTimer = null;
    }
  }

  private buildValidValueSearchIndex(): void {
    this.validValueSearchIndex.clear();
    this.validValues.forEach(validValue => {
      const optionsText = validValue.enumValidValueItems?.map(item => item.ItemDisplayValue || '').join(' ') || '';
      this.validValueSearchIndex.set(
        validValue.ValidValueId,
        `${validValue.ValidValueName || ''} ${optionsText}`.toLowerCase()
      );
    });
  }

  private applyValidValueFilter(): void {
    const query = this.validValueSearchQuery.trim().toLowerCase();

    if (!query) {
      this.filteredValidValues = this.validValues;
      return;
    }

    this.filteredValidValues = this.validValues.filter(validValue =>
      this.validValueSearchIndex.get(validValue.ValidValueId)?.includes(query)
    );
  }

  private applyDefaultValueFilter(): void {
    const items = this.valueTriggerList.length > 0
      ? this.valueTriggerList
      : (this.selectedValidValue?.enumValidValueItems || []);
    const query = this.defaultValueSearchQuery.trim().toLowerCase();

    if (!query) {
      this.filteredSelectedValidValueItems = items;
      return;
    }

    this.filteredSelectedValidValueItems = items.filter(item =>
      item.ItemDisplayValue?.toLowerCase().includes(query)
    );
  }

  getDefaultValueDisplayText(validValueItemId: string): string {
    if (!validValueItemId || !this.selectedValidValue?.enumValidValueItems?.length) {
      return '';
    }

    const selectedItem = this.selectedValidValue.enumValidValueItems.find(item => item.ValidValueItemId === validValueItemId);
    return selectedItem?.ItemDisplayValue || '';
  }

  /**
   * Case-insensitive "contains" filter used by every in-panel dropdown search.
   * An empty query returns the source list untouched; a missing source returns
   * an empty list so templates can always read .length safely.
   */
  private filterOptions<T>(source: T[], query: string, textOf: (option: T) => string): T[] {
    const trimmedQuery = query.trim().toLowerCase();

    if (!source) {
      return [];
    }

    if (!trimmedQuery) {
      return source;
    }

    return source.filter(option => (textOf(option) || '').toLowerCase().includes(trimmedQuery));
  }

  /** Restores every dropdown to its full, unfiltered option list. */
  private resetSelectSearchFilters(): void {
    this.onControlTypeSearchInput('');
    this.onParentItemSearchInput('');
    this.onBeforeActionSearchInput('');
    this.onAfterActionSearchInput('');
    this.onValueTriggerSearchInput('');
  }

  /**
   * Moves focus into a dropdown's search box once mat-select has finished
   * opening, so the user can type immediately.
   */
  focusSelectSearch(searchInput: HTMLInputElement): void {
    setTimeout(() => searchInput.focus());
  }

  /**
   * Lets navigation keys bubble to mat-select's key manager so arrow/enter
   * option navigation keeps working, while stopping every other key so the
   * select's built-in type-ahead does not swallow what is typed in the search
   * box.
   */
  onSelectSearchKeydown(event: KeyboardEvent): void {
    const navigationKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'];

    if (!navigationKeys.includes(event.key)) {
      event.stopPropagation();
    }
  }

  onControlTypeSearchInput(searchText: string): void {
    this.controlTypeSearchQuery = searchText;
    this.filteredControlTypes = this.filterOptions(
      this.cfgFormItemTypeList, searchText, controlType => controlType.name);
  }

  onParentItemSearchInput(searchText: string): void {
    this.parentItemSearchQuery = searchText;
    this.filteredOtherItemsInForm = this.filterOptions(
      this.otherItemsInForm, searchText, otherItem => otherItem.DFormItemName);
  }

  onBeforeActionSearchInput(searchText: string): void {
    this.beforeActionSearchQuery = searchText;
    this.filteredBeforeActions = this.filterOptions(
      this.actionList, searchText, action => action.ActionName);
  }

  onAfterActionSearchInput(searchText: string): void {
    this.afterActionSearchQuery = searchText;
    this.filteredAfterActions = this.filterOptions(
      this.actionList, searchText, action => action.ActionName);
  }

  onValueTriggerSearchInput(searchText: string): void {
    this.valueTriggerSearchQuery = searchText;
    this.filteredValueTriggers = this.filterOptions(
      this.valueTriggerList, searchText, valueTrigger => valueTrigger.ItemDisplayValue);
  }

  onControlTypeSelectionChange(event: MatSelectChange): void {
    const controlType = this.cfgFormItemTypeList.find(type => type.id === event.value);
    if (controlType) {
      this.selectControlType(controlType);
    }
  }

  // The "Clear ..." entry at the bottom of each panel is a mat-option with a null value, so a null
  // selection here means the user chose to clear, not that the lookup failed.
  onParentItemSelectionChange(event: MatSelectChange): void {
    if (event.value == null) {
      this.clearParentItem();
      return;
    }

    const parentItem = this.otherItemsInForm.find(otherItem => otherItem.DFormItemId === event.value);
    if (parentItem) {
      this.selectParentItem(parentItem);
    }
  }

  onBeforeActionSelectionChange(event: MatSelectChange): void {
    if (event.value == null) {
      this.removeBeforeAction();
      return;
    }

    const action = this.actionList.find(listedAction => listedAction.ItemActionId === event.value);
    if (action) {
      this.selectBeforeAction(action);
    }
  }

  onAfterActionSelectionChange(event: MatSelectChange): void {
    if (event.value == null) {
      this.removeAfterAction();
      return;
    }

    const action = this.actionList.find(listedAction => listedAction.ItemActionId === event.value);
    if (action) {
      this.selectAfterAction(action);
    }
  }

  selectBeforeAction(action: FormItemActionConfigurationModel) {
    this.selectedBeforeActionId = action.ItemActionId;
    this.item.BeforeDisplayItemAction = null;
    this.item.BeforeDisplay_ItemActionId = this.selectedBeforeActionId;
  }

  selectAfterAction(action: FormItemActionConfigurationModel) {
    this.selectedAfterActionId = action.ItemActionId;
    this.item.AfterUpdateItemAction = null;
    this.item.AfterUpdate_ItemActionId = this.selectedAfterActionId;
  }

  removeBeforeAction() {
    this.item.BeforeDisplayItemAction = null;
    this.item.BeforeDisplay_ItemActionId = null;
    this.selectedBeforeActionId = null;
  }

  removeAfterAction() {
    this.item.AfterUpdateItemAction = null;
    this.item.AfterUpdate_ItemActionId = null;
    this.selectedAfterActionId = null;
  }

  clearParentItem() {
    this.item.ParentDFormItemId = null;
    this.selectedParentItemId = null;
  }

  clearValueTrigger() {
    this.item.NoteTriggerValidValueItemId = null;
    this.selectedValueTriggerId = null;
  }

  setupAlignmentList() {
    this.cfgItemAlignmentTypeList = [
      { name: 'Left', id: '00000036-1000-0000-0000-000000000001' },
      { name: 'Right', id: '00000036-1000-0000-0000-000000000002' },
      { name: 'Center', id: '00000036-1000-0000-0000-000000000003' },
    ];
  }

  selectAlignmentType(alignmentTypeOrId) {
    this.item.enumLabelAligmentID = typeof alignmentTypeOrId === 'string' ? alignmentTypeOrId : alignmentTypeOrId.id;
    this.item.enumLabelAligment = null;
  }

  returnItemTypeIcon(item: FormItemConfigurationModel) {
    return this.cfgFormItemTypeList.find(itemType => {
      return itemType.id == item.enumFormControlID;
    }).icon;
  }

  setupItemTypeList() {
    this.cfgFormItemTypeList = FormsConstants.cfgFormItemTypeList;
    this.cfgFormItemTypeList = this.cfgFormItemTypeList.sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) { return -1; }
      if (a.name.toLowerCase() > b.name.toLowerCase()) { return 1; }
      return 0;
    });
  }

  fetchAllActions() {
    const promise = new Promise((resolve, reject) => {
      this.formsBuilderService.getAllActions()
        .subscribe(response => {
          resolve(response);
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  fetchAllValidValues() {
    const promise = new Promise((resolve, reject) => {
      this.formsBuilderService.getAllValidValues()
        .subscribe(response => {
          resolve(response);
        }, err => {
          reject(err);
        });
    });
    return promise;
  }

  getValidValuePreview(validValue: EnumValidValueModel) {
    return validValue.enumValidValueItems.map(vv => vv.ItemDisplayValue).join(', ');
  }

  checkOptions() {

    const validValueControlFound = this.canValidValueControlTypes.find(validValue => {
      return validValue.toLowerCase() === this.item.enumFormControlID.toLowerCase();
    });
    this.canUseValidValues = validValueControlFound === undefined;

    const defaultValueControlFound = this.canUseDefaultValueTypes.find(validValue => {
      return validValue.toLowerCase() === this.item.enumFormControlID.toLowerCase();
    });
    this.canUseDefaultValue = defaultValueControlFound === undefined;


    const canUseAfterUpdateControlTypes = [
      this.globals.CONTROL_GROUP_HEADING_WITH_SUMMARY,
      this.globals.CONTROL_HEADING,
      this.globals.CONTROL_LABEL,
      this.globals.CONTROL_LABEL_45,
      this.globals.CONTROL_ROW_HEADER,
      this.globals.CONTROL_SPACER,
      this.globals.CONTROL_MARKUP,
    ];

    this.canUseAfterUpdateAction = true;
    canUseAfterUpdateControlTypes.forEach(validValue => {
      if (validValue.toLowerCase() === this.item.enumFormControlID.toLowerCase()) {
        this.canUseAfterUpdateAction = false;
      }
    });

    const canSelectDefaultValueFromValidValuesType = [
      this.globals.CONTROL_ACTION_BUTTON,
      this.globals.CONTROL_BLOOD_PRESSURE,
      this.globals.CONTROL_CURRENCY,
      this.globals.CONTROL_DATE,
      this.globals.CONTROL_DATE_TIME,
      this.globals.CONTROL_GROUP_HEADING_WITH_SUMMARY,
      this.globals.CONTROL_HEADING,
      this.globals.CONTROL_LABEL,
      this.globals.CONTROL_LABEL_45,
      this.globals.CONTROL_NUMERIC_DECIMAL,
      this.globals.CONTROL_NUMERIC_DRAG_INPUT_SLIDER,
      this.globals.CONTROL_NUMERIC_INTEGER,
      this.globals.CONTROL_NUMERIC_SLIDER,
      this.globals.CONTROL_ROW_HEADER,
      this.globals.CONTROL_SIGNATURE,
      this.globals.CONTROL_SPACER,
      this.globals.CONTROL_TEXT_AREA,
      this.globals.CONTROL_TEXT_BOX,
      this.globals.CONTROL_TIME,
      this.globals.CONTROL_VITAL_SIGNS,
      this.globals.CONTROL_PHONE,
      this.globals.CONTROL_HIDDENINPUT,
      this.globals.CONTROL_MARKUP,
      this.globals.CONTROL_BULLETPOINT,
    ];

    this.canSelectDefaultFromValidValue = true;
    canSelectDefaultValueFromValidValuesType.forEach(validValue => {
      if (validValue.toLowerCase() === this.item.enumFormControlID.toLowerCase()) {
        this.canSelectDefaultFromValidValue = false;
      }
    });

  }

  duplicate() {
    this.dialogRef.close({ delete: false, item: this.item, duplicate: true });
  }

  save() {
    console.log('save', this.item);
    this.dialogRef.close({ delete: false, item: this.item, duplicate: false });
  }

  cancel() {
    this.dialogRef.close({ delete: false, item: this.originalItem, duplicate: false });
  }

  delete() {
    this.dialogRef.close({ delete: true, duplicate: false });
  }

  openHelp(startStepId: string = 'item-configuration'): void {
    this.dialog.open(FormsBuilderTutorialComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      panelClass: 'tutorial-dialog',
      data: { startStepId }
    });
  }

  selectValueTrigger(valueTrigger) {
    console.log('selectValueTrigger executed', valueTrigger);
    this.selectedValueTriggerId = valueTrigger.ValidValueItemId;
    this.item.NoteTriggerValidValueItemId = this.selectedValueTriggerId;
  }

  onValueTriggerSelectionChange(event: MatSelectChange) {
    console.log('onValueTriggerSelectionChange executed', event);
    if (event.value == null) {
      this.clearValueTrigger();
      return;
    }

    const selectedValueItemId = event.value;
    const selectedValueTrigger = this.valueTriggerList.find(vt => vt.ValidValueItemId === selectedValueItemId);
    if (selectedValueTrigger) {
      this.selectValueTrigger(selectedValueTrigger);
    }
  }

}

